/**
 * searchFormRules.js — LUẬT THUẦN của FORM TÌM KIẾM RIÊNG trong DataListManager.
 *
 * Tương đương `search_form` của listinfo (InputBase.openSearch + SearchForm.js): màn hình khai MỘT bộ
 * field chỉ dùng để tìm kiếm — khác hẳn các cột của bản ghi (vd "từ ngày / đến ngày", "kho", "khách
 * hàng", "chỉ chứng từ chưa duyệt"). Mỗi field biết cách tự dịch giá trị sang điều kiện Mongo.
 *
 * Nguồn field (DataListManager chọn):
 *   `searchFields` (mảng field khai thẳng trong data_list_config)            — thắng
 *   `searchForm`   (mã một màn hình khác: data_list_config trước, listinfo sau) — nạp về lấy `fields`
 *
 * Luật dịch một field -> điều kiện (theo thứ tự):
 *   1. `searchCondition` (← `handle_search_condition`) là SCRIPT: nhận (condition, value, moment,
 *      numeral, formData, context) và SỬA THẲNG vào `condition`. Thắng mọi luật khác.
 *   2. field tạm (`isTemp`) -> bỏ qua (chỉ để tính toán trên form).
 *   3. `DateFilter` -> khoảng ngày trên `searchTarget` (mặc định chính tên field).
 *   4. `NumberRange` {min,max} -> $gte/$lte.
 *   5. mảng -> $in.
 *   6. chuỗi: field chọn từ danh mục (api/options) hoặc `exactSearch` -> so khớp đúng; còn lại tìm gần
 *      đúng không phân biệt hoa thường (đúng như InputBase.search).
 *   7. còn lại (số, boolean, ngày đơn) -> so khớp đúng; ngày đơn (`Date`) -> cả ngày đó.
 *
 * Cặp `tu_ngay` + `den_ngay` (hoặc `tu_ngay_gio` + `den_ngay_gio`) của listinfo KHÔNG khai script:
 * SearchForm.js cũ tự đổi thành "kỳ" và lọc `ngay_ct`. Ở đây gộp thành MỘT field `DateFilter` (từ-đến +
 * các kỳ nhanh: hôm nay, tháng này…) lọc trên `ngay_ct` — xem `prepareSearchFields`.
 *
 * KHÔNG import gì — thư viện tiêm qua `deps` ({ buildDateCondition, moment, numeral }).
 */

const DATE_PAIRS = [['tu_ngay', 'den_ngay'], ['tu_ngay_gio', 'den_ngay_gio']];
const PERIOD_FIELD = '__ky_tim_kiem';

function isEmptySearchValue(v) {
	if (v === undefined || v === null || v === '') return true;
	if (Array.isArray(v)) return v.length === 0;
	if (typeof v === 'object' && !(v instanceof Date)) {
		if ('type' in v) return v.type === 'all' || v.type === undefined && Object.keys(v).length <= 1;
		if ('min' in v || 'max' in v) return (v.min === '' || v.min === undefined || v.min === null) && (v.max === '' || v.max === undefined || v.max === null);
	}
	return false;
}

/**
 * Chuẩn bị bộ field của form tìm kiếm:
 *  - bỏ `required` dạng script chưa biên dịch? KHÔNG — giữ nguyên khai báo (form tìm kiếm của listinfo
 *    được phép bắt buộc nhập, vd bắt chọn kỳ);
 *  - bỏ các khoá chỉ có nghĩa với form nhập (`editableInList`, `formTab`, `formStep`) để form tìm kiếm
 *    luôn là MỘT lưới phẳng;
 *  - gộp cặp từ ngày/đến ngày thành một `DateFilter` (xem đầu file).
 * Trả mảng MỚI, không sửa mảng vào.
 * @param opts.dateTarget  cột ngày đem lọc cho cặp từ-đến (mặc định `ngay_ct`)
 */
function prepareSearchFields(fields, opts) {
	const o = opts || {};
	let list = (Array.isArray(fields) ? fields : []).filter(f => f && f.field).map(f => {
		const c = { ...f };
		delete c.editableInList; delete c.formTab; delete c.formStep; delete c.stepLabel;
		if (c.type === 'ObjectArray') return null;   // lưới chi tiết không có nghĩa trong form tìm kiếm
		return c;
	}).filter(Boolean);

	for (const [fromName, toName] of DATE_PAIRS) {
		const from = list.find(f => f.field === fromName);
		const to = list.find(f => f.field === toName);
		if (!from || !to) continue;
		if (from.searchCondition || to.searchCondition) continue;   // tự khai cách lọc thì tôn trọng
		if (list.some(f => f.field === PERIOD_FIELD)) break;
		const idx = Math.min(list.indexOf(from), list.indexOf(to));
		const period = {
			field: PERIOD_FIELD,
			label: o.periodLabel || 'Thời gian',
			type: 'DateFilter',
			mapTo: [fromName, toName],
			searchTarget: o.dateTarget || 'ngay_ct',
			required: !!(from.required || to.required),
			default: { type: 'thisMonth' },
			gridEdit: { xs: 12 },
			isPeriod: true,
		};
		list = list.filter(f => f !== from && f !== to);
		list.splice(idx, 0, period);
		break;
	}
	return list;
}

/**
 * Giá trị form tìm kiếm -> điều kiện Mongo.
 * @param values  dữ liệu form tìm kiếm
 * @param fields  kết quả `prepareSearchFields`
 * @param deps    { buildDateCondition(value, fieldName), moment, numeral, context }
 * @returns {{ condition: object, errors: string[] }}
 */
function buildSearchCondition(values, fields, deps) {
	const d = deps || {};
	const condition = {};
	const errors = [];
	const and = [];
	const v0 = values || {};
	for (const f of (fields || [])) {
		const name = f.field;
		const v = v0[name];
		if (typeof f.searchCondition === 'function') {
			if (isEmptySearchValue(v) && !f.searchConditionOnEmpty) continue;
			try { f.searchCondition(condition, v, d.moment, d.numeral, v0, d.context); }
			catch (e) { errors.push(`${name}: ${(e && e.message) || e}`); }
			continue;
		}
		if (f.isTemp || isEmptySearchValue(v)) continue;
		const target = f.searchTarget || name;

		if (f.type === 'DateFilter') {
			const c = typeof d.buildDateCondition === 'function' ? d.buildDateCondition(v, target) : null;
			if (c) and.push(c);
			continue;
		}
		if (f.type === 'NumberRange' || (typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && ('min' in v || 'max' in v))) {
			const c = {};
			if (v.min !== '' && v.min !== undefined && v.min !== null) c.$gte = Number(v.min);
			if (v.max !== '' && v.max !== undefined && v.max !== null) c.$lte = Number(v.max);
			if (Object.keys(c).length) and.push({ [target]: c });
			continue;
		}
		if (Array.isArray(v)) { condition[target] = { $in: v }; continue; }
		if (f.type === 'Date' && typeof d.buildDateCondition === 'function') {
			const c = d.buildDateCondition({ type: 'custom', fromDate: v, toDate: v }, target);
			if (c) and.push(c);
			continue;
		}
		if (typeof v === 'string') {
			const exact = f.exactSearch || !!f.api || (Array.isArray(f.options) && f.options.length) ||
				f.type === 'Select' || f.type === 'Autocomplete' || f.type === 'Radio' || name === '_id';
			condition[target] = exact ? v : { $regex: v.trim(), $options: 'i' };
			continue;
		}
		condition[target] = v;
	}
	if (and.length) {
		// Điều kiện khoảng (ngày/số) gộp bằng $and để hai field cùng nhắm một cột không ghi đè nhau.
		const rest = Object.keys(condition).length ? [condition] : [];
		return { condition: and.length + rest.length === 1 ? and[0] : { $and: [...rest, ...and] }, errors };
	}
	return { condition, errors };
}

/** Giá trị mặc định của form tìm kiếm (trước khi chạy script `default`). */
function searchFormInitialValues(fields, deps) {
	const d = deps || {};
	const out = {};
	for (const f of (fields || [])) {
		if (f.type === 'DateFilter') {
			const t = (f.default && f.default.type) || 'all';
			const range = (t !== 'all' && t !== 'custom' && typeof d.getDateRangeByType === 'function') ? d.getDateRangeByType(t) : {};
			const [fromKey, toKey] = Array.isArray(f.mapTo) && f.mapTo.length === 2 ? f.mapTo : ['fromDate', 'toDate'];
			out[f.field] = { type: t, [fromKey]: range.fromDate || '', [toKey]: range.toDate || '' };
		} else if (f.default !== undefined && typeof f.default !== 'function' && !(typeof f.default === 'string' && f.default.indexOf('return') >= 0)) {
			out[f.field] = f.default;
		}
	}
	return out;
}

export { PERIOD_FIELD, isEmptySearchValue, prepareSearchFields, buildSearchCondition, searchFormInitialValues };
